import React, { useState, useEffect, useMemo } from 'react';
import { Search, X, AlertTriangle, Info } from 'lucide-react';
import jobCategoryService from '../services/jobCategoryService';

const JobCategoryPicker = ({ 
  selectedCategoryId, 
  onCategorySelect, 
  error,
  disabled = false 
}) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Update selected category when prop changes
  useEffect(() => {
    if (selectedCategoryId && categories.length > 0) {
      const category = categories.find(cat => cat.id === selectedCategoryId);
      if (category) {
        setSelectedCategory(category);
        setInputValue(category.category_name);
      }
    } else if (!selectedCategoryId) {
      setSelectedCategory(null);
      setInputValue('');
    }
  }, [selectedCategoryId, categories]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const allCategories = await jobCategoryService.getJobCategories();
      setCategories(allCategories);
    } catch (error) {
      console.error('Error loading job categories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter categories based on search term
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) {
      return categories;
    }
    
    return categories.filter(category => 
      category.full_path.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.category_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [categories, searchTerm]);

  const handleInputClick = () => {
    if (!disabled && !loading) {
      setShowModal(true);
      setSearchTerm('');
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (value.trim() && !showModal) {
      setShowModal(true);
    }
    
    setSearchTerm(value);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setInputValue(category.category_name);
    setShowModal(false);
    setSearchTerm('');
    onCategorySelect(category.id, category.full_path);
  };

  const handleClear = () => {
    setSelectedCategory(null);
    setInputValue('');
    onCategorySelect('', '');
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSearchTerm('');
    // Restore input value to selected category if one exists
    if (selectedCategory) {
      setInputValue(selectedCategory.category_name);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleModalClose();
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Job Type *
          </label>
          <div className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-pest-blue mr-3"></div>
              Loading job types...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Job Type *
        </label>

        <div className="relative">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onClick={handleInputClick}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors cursor-pointer ${
              error ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'
            } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            placeholder="Start typing or click to select job type..."
            readOnly={!showModal}
          />
          
          {selectedCategory && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
          )}
          
          {!selectedCategory && !disabled && (
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          )}
        </div>

        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
      </div>

      {/* Warning for dangerous species */}
      {selectedCategory && selectedCategory.full_path.includes('Brazilian Wandering Spider') && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800 mb-1">⚠️ DANGER WARNING</p>
              <p className="text-sm text-red-700">
                This is a highly venomous spider species. Full PPE and specialist approach required.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal Overlay */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-start justify-center z-50 pt-8 overflow-y-auto" onClick={handleModalClose}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden m-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white">Select Job Type</h2>
                <p className="text-sm text-white/90 mt-1">
                  {filteredCategories.length} type{filteredCategories.length !== 1 ? 's' : ''} available - Click to select
                </p>
              </div>
              <button
                onClick={handleModalClose}
                className="text-white hover:text-gray-200 transition-colors duration-200"
                aria-label="Close job type selection"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Search Field */}
            <div className="p-4 md:p-6 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
                  placeholder="Search job types by name or description..."
                  autoFocus
                />
              </div>
            </div>
            
            {/* Categories List */}
            <div className="p-4 md:p-6 overflow-y-auto flex-1 overscroll-contain">
              {filteredCategories.length > 0 ? (
                <div className="space-y-2">
                  {filteredCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategorySelect(category)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 hover:bg-blue-50 hover:border-blue-300 ${
                        selectedCategory?.id === category.id 
                          ? 'bg-blue-100 border-blue-400' 
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 mb-2">
                            {category.full_path}
                          </div>
                          {category.description && (
                            <div className="text-sm text-gray-600">
                              {category.description}
                            </div>
                          )}
                        </div>
                        {category.full_path.includes('Brazilian Wandering Spider') && (
                          <div className="ml-3 flex-shrink-0">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg mb-2">No job types found</p>
                  <p className="text-gray-400 text-sm">
                    Try adjusting your search terms or browse all available job types
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobCategoryPicker;